import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { client } from "@/lib/api";
import { moneyFull, pct, num } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Copy, RefreshCcw, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageToggle from "@/components/LanguageToggle";
import AppFooter from "@/components/AppFooter";

const REGION_LABEL = {
  north_america: "North America", europe: "Europe", nordic: "Nordic",
  asia_pacific: "Asia Pacific", latin_america: "LATAM", middle_east_africa: "MEA", global: "Global",
};

export default function Cohort() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reseeding, setReseeding] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await client.get("/cohort/list");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Could not load cohort", { description: e?.message });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function reseed() {
    setReseeding(true);
    try {
      const r = await client.post("/cohort/seed");
      toast.success(`Seed complete · ${r.data.inserted.length} added, ${r.data.skipped.length} skipped`);
      await load();
    } catch (e) {
      toast.error("Seed failed", { description: e?.message });
    } finally {
      setReseeding(false);
    }
  }

  function copyLink(slug) {
    const url = `${window.location.origin}/report/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Report link copied");
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.24em] font-semibold">PressImpact</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Button
              data-testid="reseed-btn"
              onClick={reseed}
              disabled={reseeding}
              variant="outline"
              className="rounded-sm border-slate-200"
            >
              <RefreshCcw className={`h-4 w-4 mr-2 ${reseeding ? "animate-spin" : ""}`} />
              {reseeding ? "Seeding…" : "Reseed"}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <section className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Users className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
            <p className="text-xs tracking-[0.28em] uppercase text-slate-500 font-semibold">
              Nordic MarTech · Launch Cohort
            </p>
          </div>
          <h1 className="font-heading font-light text-4xl md:text-5xl tracking-tight text-slate-900 max-w-3xl">
            Twelve warm workspaces, one link each.
          </h1>
          <p className="mt-4 text-slate-600 max-w-2xl">
            Each launch customer has a pre-generated report waiting on a private link.
            Share the URL below and their first session opens on a fully populated
            dashboard — no empty forms, no configuration.
          </p>
        </section>

        {loading ? (
          <div className="text-slate-500 text-sm">Loading cohort…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cohort-grid">
            {items.map((it) => {
              const roi = it.results?.base?.roi_pct ?? 0;
              const roiPos = roi >= 0;
              return (
                <div
                  key={it.slug}
                  data-testid={`cohort-card-${it.slug}`}
                  className="border border-slate-200 bg-white p-6 hover:border-slate-400 transition-colors flex flex-col"
                >
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <div className="text-[10px] tracking-[0.24em] uppercase text-slate-400 font-semibold mb-1">
                        {it.model_type?.toUpperCase()} · {it.industry} · {it.wire_tier}
                      </div>
                      <h3 className="font-heading text-lg font-medium text-slate-900 leading-tight">
                        {it.cohort_member}
                      </h3>
                      <p className="text-xs text-slate-500 mt-1">{it.author_email}</p>
                    </div>
                    <div className={`font-heading text-2xl font-light ${roiPos ? "text-slate-900" : "text-red-600"} whitespace-nowrap`}>
                      {pct(roi)}
                    </div>
                  </div>

                  <p className="text-sm text-slate-700 leading-relaxed mb-4 flex-1">{it.report_name}</p>

                  <div className="grid grid-cols-3 gap-2 mb-5 border-t border-slate-100 pt-4">
                    <MiniStat label="Net" value={moneyFull(it.results?.base?.net_benefit, it.currency)} />
                    <MiniStat label="Reach" value={num(it.results?.base?.scorecard?.media_reach)} />
                    <MiniStat label="Region" value={REGION_LABEL[it.region] || it.region} tone={it.nordic_fallback ? "warn" : "neutral"} />
                  </div>

                  <div className="flex items-center gap-2">
                    <Link
                      to={`/report/${it.slug}`}
                      data-testid={`cohort-open-${it.slug}`}
                      className="flex-1"
                    >
                      <Button variant="outline" className="w-full rounded-sm border-slate-200 hover:bg-slate-900 hover:text-white transition-colors">
                        <ExternalLink className="h-3.5 w-3.5 mr-2" /> Open report
                      </Button>
                    </Link>
                    <Button
                      data-testid={`cohort-copy-${it.slug}`}
                      variant="outline"
                      onClick={() => copyLink(it.slug)}
                      className="rounded-sm border-slate-200"
                      title="Copy report link"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      <AppFooter />
    </div>
  );
}

function MiniStat({ label, value, tone = "neutral" }) {
  const color = tone === "warn" ? "text-amber-700" : "text-slate-900";
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className={`text-xs font-medium mono mt-1 ${color}`}>{value}</div>
    </div>
  );
}
